import { ref, shallowRef } from 'vue'

import { useI18nAddon } from '@/i18n/i18n'
import type { AdLoader } from '@/modules/adv/ad-loader'
import type { AdService } from '@/modules/adv/ad-service'
import type { OutOfPageAdType } from '@/modules/adv/types'
import type { RumMetricResult } from '@/modules/analytics/types'
import { useCloudSave } from '@/modules/cloud-saves'
import { createInGamePayments, type ChargeUserFunction } from '@/modules/in-game-payments'
import { createOrderPricing } from '@/modules/in-game-payments/pricing'
import { MainFrameService } from '@/modules/main-frame'
import { GameAdHandler } from '@/modules/main-frame/ads/game-ad-handler'
import type { AdBlockerHandler, BannerPositionSizeRaw } from '@/modules/main-frame/ads/game-ad-handler.types'
import { MiniplayIntegration } from '@/modules/main-frame/custom-integrations/miniplay'
import { XiaomiIntegration } from '@/modules/main-frame/custom-integrations/xiaomi'
import { MessageAction } from '@/modules/main-frame/message-types'
import { createSafeStorage } from '@/modules/safe-storage'
import { UserService } from '@/modules/user-service'
import { addRedirectCounter } from '@/router/query-params'
import { useAdBlockStore } from '@/store/ad-block-store'
import { useAdBlockerOffer } from '@/store/ad-blocker-store'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useLoggerStore } from '@/store/logger'
import type { APIGameType, GameAttributes, User } from '@/types'
import type { Config } from '@/utils/clid-config'
import { useAPI } from '@/utils/requests/api-plugin'

import type { AdInfo } from '../types'
import { getAdLoader, LOADER_FILLER_DURATION_MS } from '../utils'
import { useDisabledAdFiller } from './use-disabled-ad-filler'
import { useGameAnalyticsEvents } from './use-game-analytics-event'
import { useLoginPromptTrigger } from './use-login-prompt-trigger'

// games send level_completed and ask for an interstitial right after - let the ad claim the screen first
const LEVEL_AD_GRACE_MS = 1000

export type GameFrame = ReturnType<typeof useGameFrame>

type GameFrameUserApi = {
    authorizeUser: () => Promise<User | null>
    getUser: () => Promise<User | null>
    isGuest: () => Promise<boolean>
    chargeUser: ChargeUserFunction
    cancelCharge: (reason: string) => void
}

export type InitGameFrameOptions = {
    game: APIGameType
    gameUrl: URL
    adConfig: Config
    adService: AdService
    userApi: GameFrameUserApi
    attributes: GameAttributes
    adBlockerOfferEnabled: boolean
    affiliateLoader: boolean
    adLoaderSubtitlesByAdType?: Partial<Record<OutOfPageAdType, string>>
    getAdRootElementId: () => string | undefined
}

/**
 * Everything between "we have a game and an ad service" and "the game iframe is running":
 * AdLoader, GameAdHandler, MainFrameService, cloud saves, safe storage, in-game payments,
 * ad state events (instream data, brand preloader errors, RUM). Shared by the export page
 * and the direct game frame on the portal.
 */
export function useGameFrame() {
    const appStore = useAppStore()
    const clientAppStore = useClientAppStore()
    const loggerStore = useLoggerStore()
    const adBlockStore = useAdBlockStore()
    const adDisabledStore = useAdDisabledStore()

    const i18NAddon = useI18nAddon()
    const { createCloudSave, getCloudSaveApi } = useCloudSave()
    const api = useAPI()
    const { signedRequest, confirmIgpDelivery, getIgpPurchases, consumeIgpPurchase } = api
    const { showOffer } = useAdBlockerOffer()
    const gameAnalyticsEventsController = useGameAnalyticsEvents()

    const gameSrc = ref('')
    // Bumped whenever the game iframe is replaced or torn down, so a purchase quoted in an
    // earlier iframe (e.g. before the post-login reload) cannot open the payment modal
    let gameSession = 0
    const isGameFrameVisible = ref(false)
    const advancedBanners = ref<BannerPositionSizeRaw[]>([])
    const brandPreloaderHasError = ref(false)
    const brandPreloaderAdType = ref<OutOfPageAdType | null>(null)
    const lastAdResult = ref<OutOfPageAdType>()
    const sidebarRefreshKey = ref(0)
    const isFocusOverlayVisible = ref(false)
    const adLoader = ref<AdLoader>()
    const gameAdHandler = shallowRef<GameAdHandler>()
    const mainFrameService = ref<MainFrameService>()

    const fullscreenAdStarted = ref(0)

    const {
        trigger: loginPromptTrigger,
        request: requestLoginPrompt,
        flush: flushLoginPrompt,
        dispose: disposeLoginPrompt,
    } = useLoginPromptTrigger(() => !!gameAdHandler.value?.isFullscreenAdOnScreen)

    const currentMainCategory = ref<string>()

    const lastSaveEvent = ref<{ type: 'cloud-save' | 'storage'; action: MessageAction } | null>(null)
    const onSaveEvent = (data: { type: 'cloud-save' | 'storage'; action: MessageAction }) => {
        lastSaveEvent.value = data
    }

    const adInfo = ref<AdInfo>({
        isVisible: false,
        secondsLeft: 0,
    })

    const sendGameLoadingRUM = shallowRef<(status: RumMetricResult) => void>()

    function updateAdInfo(patch: Partial<AdInfo>) {
        adInfo.value = { ...adInfo.value, ...patch }
    }

    function resetAdInfo() {
        updateAdInfo({
            isVisible: false,
            secondsLeft: 0,
            podCounter: undefined,
        })
    }

    const { stopDisabledAdFiller, adDisabledHandler, isDisabledAdFillerVisible } = useDisabledAdFiller()

    function startGameLoading() {
        if (!isGameFrameVisible.value) {
            isGameFrameVisible.value = true

            // start counting when game starts loading
            gameAnalyticsEventsController.startTimers(currentMainCategory.value)

            gameAdHandler.value?.startMidrollTimer()

            sendGameLoadingRUM.value = loggerStore.startRumEvent({
                operationName: 'game-loading',
            })
        }
    }

    function resetErrorPreload() {
        brandPreloaderHasError.value = false
        brandPreloaderAdType.value = null
    }

    function showPreroll() {
        gameAdHandler.value?.showPreroll()
    }

    function destroy() {
        gameSession += 1
        mainFrameService.value?.destroy()
        gameAdHandler.value?.stopMidrollTimer()
        disposeLoginPrompt()
    }

    function initGameFrame({
        game,
        gameUrl,
        adConfig,
        adService,
        userApi,
        adBlockerOfferEnabled,
        affiliateLoader,
        adLoaderSubtitlesByAdType,
        getAdRootElementId,
        attributes,
    }: InitGameFrameOptions) {
        currentMainCategory.value = game.main_category

        adInfo.value.debranding = adConfig.isCustomized

        gameSession += 1

        gameSrc.value = gameUrl.href

        const cloudSaveApi = getCloudSaveApi(game.id)
        const cloudSaveMessageHandler = createCloudSave(game.id, game.hru, onSaveEvent, {
            isGuest: userApi.isGuest,
        })
        const storageMessageHandler = createSafeStorage(game.hru, userApi.getUser, cloudSaveApi, onSaveEvent)

        const miniplayIntegration = new MiniplayIntegration({
            queryParams: appStore.initialQuery,
            gameHRU: game.hru,
            storageMessageHandler,
        })
        const xiaomiIntegration = new XiaomiIntegration({
            queryParams: appStore.initialQuery,
        })

        const adBlockerHandler: AdBlockerHandler = async (
            placement: OutOfPageAdType,
        ): Promise<{ message: 'showAd' | 'fakeShowAd' }> => {
            if (
                !adBlockerOfferEnabled ||
                placement !== 'rewarded' ||
                adBlockStore.adBlockStatus !== 'enabled' ||
                adDisabledStore.isAdDisabled
            ) {
                return { message: 'showAd' }
            }
            loggerStore.logEvent({
                event: 'custom_event',
                eventName: 'ad_blocker_offer',
            })
            try {
                const result = await showOffer()
                loggerStore.logEvent({
                    event: 'custom_event',
                    eventName: result.completed ? 'ad_blocker_offer_success' : 'ad_blocker_offer_skip',
                })
                return { message: result.completed ? 'fakeShowAd' : 'showAd' }
            } catch {
                loggerStore.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_blocker_offer_fail',
                })
                return { message: 'showAd' }
            }
        }

        adLoader.value = getAdLoader({
            affiliate: affiliateLoader,
            clid: appStore.clid,
            adConfig,
            getTimeout: (placement: OutOfPageAdType) =>
                !adDisabledStore.isAdDisabled ? adService.getAdMaxTimeout(placement) : LOADER_FILLER_DURATION_MS,
            subtitlesByAdType: adLoaderSubtitlesByAdType,
        })

        gameAdHandler.value = new GameAdHandler(adService, loggerStore.logEvent, {
            adHandlerConfig: adConfig.inGameAdHandlerConfig,
            smartAdsConfig: adConfig.isSmartAdDisabled ? undefined : game.config,
            adBlockerHandler,
            adDisabledHandler,
            adBlockDetected: () => adBlockStore.adBlockStatus === 'enabled',
            loader: adLoader.value,
            getAdRootElementId,
        })
        mainFrameService.value = new MainFrameService({
            currentLanguage: i18NAddon.getCurrentLanguage(),
            additionalPlatformInfo: {
                clid: appStore.clid,
            },
            isPaymentsSupported: appStore.isPurchaseEnabled,
            gameAdHandler: gameAdHandler.value,
            inGamePaymentsMessageHandler: createInGamePayments({
                gameId: game.id,
                gameHru: game.hru,
                gameTitle: game.title,
                chargeUser: userApi.chargeUser,
                pricing: createOrderPricing({
                    api,
                    isFiatAllowed: () => !appStore.isNativeAppEnv,
                }),
                attributes: { ...attributes },
                getSessionToken: () => gameSession,
                confirmDelivery: confirmIgpDelivery,
                getPurchases: getIgpPurchases,
                consumePurchase: consumeIgpPurchase,
            }),
            storageMessageHandler,
            cloudSaveMessageHandler,
            userService: new UserService({
                authorizeUser: userApi.authorizeUser,
                gameHru: game.hru,
                auid: clientAppStore.auid || '',
                signedRequest,
            }),
            onLogin: async () => {
                const save = await cloudSaveApi.getState().catch((error) => {
                    console.error(error)
                    return undefined
                })

                // Empty cloud: nothing to protect. Unreadable cloud: keep the session rather than
                // reloading into the same failing lookup and losing the player's progress.
                if (!save || Object.keys(save).length === 0) {
                    return
                }

                userApi.cancelCharge('Game is reloading')
                addRedirectCounter(gameUrl)
                gameSession += 1
                gameSrc.value = gameUrl.href
            },
            onGameStart: () => {
                gameAdHandler.value?.refreshInitTime()

                loggerStore.logEvent({
                    event: 'custom_event',
                    eventName: 'game_bridge_loaded',
                })

                sendGameLoadingRUM.value?.({
                    status: 'success',
                    dimension1: game.hru,
                })
            },
            onGameFocusChange: (isFocused) => {
                isFocusOverlayVisible.value = !isFocused
            },
            onPlatformMessage: (message) => {
                if (message === 'level_completed') {
                    requestLoginPrompt('level', LEVEL_AD_GRACE_MS)
                }
            },
            enableSync: appStore.isNativeIosEnv,
        })

        miniplayIntegration.listen(mainFrameService.value.eventBus)
        xiaomiIntegration.listen(mainFrameService.value.eventBus)

        gameAdHandler.value.eventBus.addEventListener('setAdvancedBanners', (event) => {
            advancedBanners.value = event.detail
        })

        gameAdHandler.value.eventBus.addEventListener('adStateChanged', (event) => {
            const data = event.detail

            switch (data.action) {
                case 'start':
                    updateAdInfo({
                        placement: data.adType,
                    })
                    fullscreenAdStarted.value++
                    break
                case 'rewarded':
                    requestLoginPrompt('reward')
                    break
                case 'closed':
                    resetAdInfo()
                    sidebarRefreshKey.value++
                    flushLoginPrompt()
                    if (data.adType === 'interstitial_preroll') {
                        startGameLoading()
                    }
                    break
                case 'empty':
                    lastAdResult.value = data.adType
                    resetAdInfo()
                    flushLoginPrompt()
                    brandPreloaderHasError.value = data.adType !== 'interstitial_preroll'
                    brandPreloaderAdType.value = data.adType
                    if (data.adType === 'interstitial_preroll') {
                        startGameLoading()
                    }
                    break
                case 'timer':
                    if (
                        data.secondsLeft !== undefined &&
                        data.secondsLeft >= 0 &&
                        data.secondsLeft !== adInfo.value.secondsLeft
                    ) {
                        updateAdInfo({
                            isVisible: true,
                            secondsLeft: data.secondsLeft,
                        })
                    }
                    break
                case 'pod_counter':
                    if (data.adPosition !== undefined && data.totalAds !== undefined) {
                        updateAdInfo({
                            isVisible: true,
                            podCounter: { adPosition: data.adPosition, totalAds: data.totalAds },
                        })
                    }
                    break
                case 'opened':
                    break
                default:
            }
        })
    }

    return {
        gameSrc,
        isGameFrameVisible,
        adInfo,
        advancedBanners,
        brandPreloaderHasError,
        brandPreloaderAdType,
        lastAdResult,
        playedMs: gameAnalyticsEventsController.playedMs,
        loginPromptTrigger,
        fullscreenAdStarted,
        requestLoginPrompt,
        sidebarRefreshKey,
        isFocusOverlayVisible,
        lastSaveEvent,
        adLoader,
        gameAdHandler,
        mainFrameService,
        isDisabledAdFillerVisible,
        stopDisabledAdFiller,
        initGameFrame,
        startGameLoading,
        resetErrorPreload,
        showPreroll,
        destroy,
    }
}
